import { FormStore, GlobalStore } from "@roc/feature-app-core";
import { action, flow, makeObservable, observable } from "mobx";
import { ReferralFeeExternalService } from "../services/referralFeeExternalFeeService";
import { GENERIC_ERROR_MESSAGE } from "@roc/feature-utils";
import { DEFAULT_LINK_ERROR, getApiErrorMessage } from "../components/referralFeeUtils";

const form = {
  fields: {
    bankName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'bank name'
    },
    accountName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'account name',
    },
    accountNumber: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'account number',
    },
    routingNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{9}$/',
      message: 'The routing number must be 9 digits.'
    },
    streetName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The Street Name field is required.',
    },
    city: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The City field is required.',
    },
    zipCode: {
      value: '',
      error: null,
      message: 'Zip Code must be 5 digits',
      rule: 'required|regex:/^\\d{5}$/|alpha_dash',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The State field is required.',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};


export class ReferralAuthStore extends FormStore {
  private referralFeeService: ReferralFeeExternalService;
  public globalStore: GlobalStore;
  public activeStep: number;
  public token: string = null;
  public entityName: string;
  public loading: boolean;
  public linkError: string = null;
  public isSigned: boolean;
  public requestComplete: boolean;

  constructor(globalStore: GlobalStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.referralFeeService = new ReferralFeeExternalService();
    this.activeStep = 0;
    this.entityName = '';
    this.loading = false;
    this.isSigned = false;
    this.requestComplete = false;
    makeObservable(this, {
      activeStep: observable,
      token: observable,
      entityName: observable,
      loading: observable,
      linkError: observable,
      isSigned: observable,
      requestComplete: observable,
      moveToSignatureStep: action,
      goPrevStep: action,
      validateLink: flow,
      submit: flow,
    });
  }

  moveToSignatureStep() {
    this.runFormValidationWithMessage();
    if (this.form.meta.isValid) {
      this.activeStep = 1;
    }
  }

  goPrevStep() {
    this.activeStep = 0;
  }

  /** Validates the signed link token and loads the broker's bank info status. */
  *validateLink(token: string) {
    this.token = token;
    this.linkError = null;
    if (!token) {
      this.linkError = DEFAULT_LINK_ERROR;
      this.requestComplete = true;
      return;
    }
    this.loading = true;
    try {
      const response = yield this.referralFeeService.validateReferralFeeLink(token);
      const linkInfo = response?.data?.data;
      if (!linkInfo?.loanId) {
        this.linkError = DEFAULT_LINK_ERROR;
        return;
      }
      this.entityName = linkInfo.entityName ?? '';
      this.isSigned = ['Pending Approval', 'Approved'].includes(linkInfo.bankInfoStatus);
      this.activeStep = this.isSigned ? 1 : 0;
    } catch (error) {
      this.linkError = getApiErrorMessage(error, DEFAULT_LINK_ERROR);
    } finally {
      this.loading = false;
      this.requestComplete = true;
    }
  }

  *submit(signature: string) {
    this.loading = true;
    try {
      const values = this.getFormValues();
      yield this.referralFeeService.generateReferralFeeDocument({
        accountName: values.accountName,
        routingNumber: values.routingNumber,
        accountNumber: values.accountNumber,
        bankName: values.bankName,
        streetName: values.streetName,
        city: values.city,
        zipCode: values.zipCode,
        state: values.state,
        signature,
        userAgent: navigator.userAgent,
      }, this.token);
      this.isSigned = true;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: getApiErrorMessage(error, GENERIC_ERROR_MESSAGE),
      });
    } finally {
      this.loading = false;
    }
  }
}
